import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    message,
    Select,
    Row,
    Col,
    Divider
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import {LeftOutlined} from "@ant-design/icons";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;

const RegistrationDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [registration, setRegistration] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchRegistration();
    }, [id]);

    const fetchRegistration = async () => {
        try {
            const res = await api.get(`/cc/registrations/${id}/`);
            const data = res.data;
            setRegistration(data);

            form.setFieldsValue({
                ...data,
                dob: data.dob ? dayjs(data.dob) : null,
                passport_issue: data.passport_issue ? dayjs(data.passport_issue) : null,
                passport_expiration: data.passport_expiration ? dayjs(data.passport_expiration) : null,
            });
        } catch (err) {
            message.error("Failed to load registration.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (values: any) => {
        try {
            const payload = {
                ...values,
                dob: values.dob.format("YYYY-MM-DD"),
                passport_issue: values.passport_issue.format("YYYY-MM-DD"),
                passport_expiration: values.passport_expiration.format("YYYY-MM-DD"),
            };
            await api.put(`/cc/registrations/${id}/`, payload);
            message.success("Registration updated successfully.");
            setIsModalVisible(false);
            fetchRegistration();
        } catch {
            message.error("Update failed.");
        }
    };

    const showEditModal = () => {
        if (registration) {
            form.setFieldsValue({
                ...registration,
                dob: registration.dob ? dayjs(registration.dob) : null,
                passport_issue: registration.passport_issue ? dayjs(registration.passport_issue) : null,
                passport_expiration: registration.passport_expiration ? dayjs(registration.passport_expiration) : null,
            });
        }
        setIsModalVisible(true);
    };

    const disablePastDates = (current: dayjs.Dayjs) => {
        return current && current < dayjs().startOf("day");
    };

    if (!registration) return null;

    return (
        <div style={{padding: 24}}>
            <Button
                icon={<LeftOutlined/>}
                onClick={() => navigate(-1)}
                style={{marginBottom: 24}}
                className="rounded-md"
            >
                Back
            </Button>

            <Title level={3} style={{marginBottom: 16}}>
                <NoWrap>Casino Club Registration Details</NoWrap>
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Customer Overview
                            </Title>
                        </Col>
                    </Row>
                }
                extra={
                    <Button type="primary" onClick={showEditModal} className="rounded-md">
                        Edit
                    </Button>
                }
                loading={loading}
            >
                <Descriptions bordered column={1} style={maxW700}>
                    <Descriptions.Item label="First Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.first_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Last Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.last_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Email"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.email}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="DOB"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.dob}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Nationality"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.nationality}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Address"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.address}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Phone"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.phone}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Passport Number"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.passport_number}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Passport Issue"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.passport_issue}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Passport Expiration"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.passport_expiration}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="MMID"
                                       labelStyle={boldLabelStyle}><NoWrap>{registration.mmid || "Pending"}</NoWrap></Descriptions.Item>
                </Descriptions>

                <Modal
                    title={<span className="text-base font-semibold">Edit Registration</span>}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    okText="Save"
                    centered
                    width={700}
                    okButtonProps={{className: "rounded-md"}}
                    cancelButtonProps={{className: "rounded-md"}}
                >
                    <Divider className="my-2"/>

                    <Form layout="vertical" form={form} onFinish={handleUpdate}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="first_name"
                                    label="First Name"
                                    rules={[{required: true, message: "Please enter first name"}]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="last_name"
                                    label="Last Name"
                                    rules={[{required: true, message: "Please enter last name"}]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="email" label="Email">
                                    <Input placeholder="you@example.com"/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="dob"
                                    label="Date of Birth"
                                    rules={[{required: true, message: "Please select date of birth"}]}
                                >
                                    <DatePicker style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>

                            <Col span={12}>
                                <Form.Item
                                    name="nationality"
                                    label="Nationality"
                                    rules={[{required: true, message: "Please enter nationality"}]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="phone"
                                    label="Phone"
                                    rules={[{required: true, message: "Please enter phone number"}]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="address"
                                    label="Address"
                                    rules={[{required: true, message: "Please enter address"}]}
                                >
                                    <Input.TextArea rows={2}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="passport_number"
                                    label="Passport Number"
                                    rules={[{required: true, message: "Please enter passport number"}]}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="passport_issue"
                                    label="Passport Issue"
                                    rules={[{required: true, message: "Passport issue date is required"}]}
                                >
                                    <DatePicker
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="passport_expiration"
                                    label="Passport Expiration"
                                    rules={[{required: true, message: "Passport expiration date is required"}]}
                                >
                                    <DatePicker
                                        style={{width: "100%"}}
                                        disabledDate={disablePastDates}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="mmid"
                                    label="MMID"
                                    rules={[
                                        {
                                            pattern: /^\d{8}$/,
                                            message: "MMID must be exactly 8 digits",
                                        },
                                        {
                                            validator: async (_, value) => {
                                                if (!value || value.length !== 8) return Promise.resolve();
                                                const response = await api.get(`/cc/mmid-exists/?mmid=${value}`);
                                                if (response.data.exists) {
                                                    return Promise.reject(new Error("This MMID already exists"));
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input placeholder="(Update When Received)" maxLength={8}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="agent" label="Agent">
                                    <Input placeholder="Agent name, if applicable"/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="source" label="Source">
                                    <Select placeholder="Select source" allowClear>
                                        <Select.Option value="Official LINE">Official LINE</Select.Option>
                                        <Select.Option value="Private LINE">Private LINE</Select.Option>
                                        <Select.Option value="Agent LINE">Agent LINE</Select.Option>
                                        <Select.Option value="Telegram">Telegram</Select.Option>
                                        <Select.Option value="Email">Email</Select.Option>
                                        <Select.Option value="Wallet">Wallet</Select.Option>
                                        <Select.Option value="Poker">Poker</Select.Option>
                                        <Select.Option value="Walk-in">Walk-in</Select.Option>
                                        <Select.Option value="Event">Event</Select.Option>
                                        <Select.Option value="Other">Other</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default RegistrationDetailPage;